// Email-output rendering kernel: tagged template literal that escapes
// interpolated user data by default, while passing trusted HtmlString
// values through un-escaped (composition). THE XSS-prevention boundary
// for the email-output pipeline. Per spec/tech-arch.md §3.2 + §12.5.

import { HtmlString } from '../types'

/**
 * Tagged template for composing trusted HTML markup.
 *
 * Interpolation rules (see `interpolate` below):
 * - `null`, `undefined`, `false`              → ''                 (conditional render)
 * - `Array`                                    → element-wise interpolation, joined
 * - `HtmlString`                               → passed through un-escaped (composition)
 * - everything else                            → coerced to string + HTML-escaped
 *
 * Returned `HtmlString` is opaque — callers extract `.value` only at the
 * document boundary in renderEmail (P-019).
 */
export function html(
  strings: TemplateStringsArray,
  ...values: unknown[]
): HtmlString {
  // forEach over `strings` keeps noUncheckedIndexedAccess (D-006) happy
  // without non-null assertions.
  let out = ''
  strings.forEach((str, i) => {
    out += str
    if (i < values.length) {
      out += interpolate(values[i])
    }
  })
  return new HtmlString(out)
}

export function isHtmlString(v: unknown): v is HtmlString {
  return v instanceof HtmlString
}

/**
 * HTML-escape the five reserved characters. Hand-rolled per OC §3.8: the
 * function is ~5 lines and the test surface is exhaustive — third-party
 * (`he`, `lodash.escape`) adds dep weight without value. See D-024.
 */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function interpolate(v: unknown): string {
  if (v == null || v === false) return ''
  if (Array.isArray(v)) return v.map(interpolate).join('')
  if (isHtmlString(v)) return v.value
  return escapeHtml(String(v))
}
